import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ActivityTimeline } from '../components/activity/ActivityTimeline';
import { User, Search, ArrowLeft } from 'lucide-react';

export default function ActivitiesPage() {
    const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
    const [selectedClientName, setSelectedClientName] = useState<string>('');
    const [searchQuery, setSearchQuery] = useState('');
    const [clients, setClients] = useState<{ id: string, company_name: string }[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const handleSearch = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!searchQuery.trim()) return;

        setLoading(true);
        setHasSearched(true);

        try {
            const { data, error } = await supabase
                .from('customers')
                .select('id, company_name')
                .ilike('company_name', `%${searchQuery}%`)
                .order('company_name')
                .limit(20);

            if (error) throw error;
            setClients(data || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectClient = (client: { id: string, company_name: string }) => {
        setSelectedClientId(client.id);
        setSelectedClientName(client.company_name);
    };

    const handleClearSelection = () => {
        setSelectedClientId(null);
        setSelectedClientName('');
        // Optional: setClients([]) or keep list? Keeping list is friendlier.
    };

    if (selectedClientId) {
        return (
            <div className="mx-auto w-full max-w-5xl py-2 md:py-6">
                <div className="mb-6">
                    <Button variant="ghost" onClick={handleClearSelection} className="pl-0 hover:pl-2 transition-all">
                        <ArrowLeft className="w-4 h-4 mr-2" /> Seleziona un altro cliente
                    </Button>
                    <h1 className="text-xl md:text-2xl font-bold mt-2 flex flex-wrap items-center gap-2 break-words">
                        <User className="w-6 h-6 text-gray-400 flex-shrink-0" />
                        <span>Attività: <span className="text-primary-600">{selectedClientName}</span></span>
                    </h1>
                </div>

                <div className="bg-white p-3 md:p-6 rounded-lg shadow-sm border">
                    <ActivityTimeline clientId={selectedClientId} />
                </div>
            </div>
        );
    }

    return (
        <div className="mx-auto w-full max-w-2xl py-6 md:py-10">
            <div className="text-center mb-6 md:mb-10">
                <h1 className="text-2xl md:text-3xl font-bold mb-2">Gestione Attività</h1>
                <p className="text-gray-500">Seleziona un cliente per visualizzare o aggiungere nuove attività.</p>
            </div>

            <Card className="p-4 md:p-6 shadow-lg border-t-4 border-t-blue-500">
                <form onSubmit={handleSearch} className="flex gap-2 mb-4 md:mb-6">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                        <Input
                            placeholder="Cerca Ragione Sociale..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9"
                            autoFocus
                        />
                    </div>
                    <Button type="submit" disabled={loading}>
                        {loading ? 'Cerca...' : 'Cerca'}
                    </Button>
                </form>

                <div className="space-y-2">
                    {loading && <div className="text-center text-gray-500 py-4">Ricerca in corso...</div>}

                    {!loading && hasSearched && clients.length === 0 && (
                        <div className="text-center text-gray-500 py-4">Nessun cliente trovato.</div>
                    )}

                    {!loading && clients.map(client => (
                        <div
                            key={client.id}
                            onClick={() => handleSelectClient(client)}
                            className="flex items-center justify-between p-3 rounded-md hover:bg-gray-50 cursor-pointer border border-transparent hover:border-gray-200 transition-colors group"
                        >
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-8 h-8 flex-shrink-0 rounded-full bg-slate-100 flex items-center justify-center text-primary-600 font-semibold group-hover:bg-primary-600 group-hover:text-white transition-colors">
                                    {client.company_name.charAt(0).toUpperCase()}
                                </div>
                                <span className="font-medium text-gray-700 group-hover:text-gray-900 break-words">{client.company_name}</span>
                            </div>
                            <Button variant="ghost" size="sm" className="flex-shrink-0 opacity-100 md:opacity-0 md:group-hover:opacity-100">
                                Seleziona
                            </Button>
                        </div>
                    ))}
                </div>
            </Card>
        </div>
    );
}
